import React, { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { setPageTitle } from "../../../redux/store/themeConfigSlice"
import {
  getWhatsAppButtonSettings,
  updateWhatsAppButtonSettings,
  WhatsAppButtonSettings,
} from "../../../services/whatsappButtonAPI"
import { getErrorStatus, getErrorResponse } from "../../../types/error"
import { useNavigate } from "react-router-dom"

const WhatsAppButtonManager = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [settings, setSettings] = useState<WhatsAppButtonSettings>({
    isVisible: true,
    approvedProfileUrl: "",
    unapprovedProfileUrl: "",
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  useEffect(() => {
    dispatch(setPageTitle("WhatsApp Button Yönetimi"))
  }, [dispatch])

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true)
        setError(null)
        const data = await getWhatsAppButtonSettings()
        setSettings(data)
      } catch (error: unknown) {
        if (getErrorStatus(error) === 401) {
          navigate("/login")
        }
        setError(`Ayarlar yüklenemedi: ${getErrorResponse(error) || "Bilinmeyen hata"}`)
      } finally {
        setLoading(false)
      }
    }

    fetchSettings()
  }, [navigate])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target
    setSettings((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccessMessage(null)

      if (settings.isVisible) {
        if (!settings.approvedProfileUrl.trim()) {
          setError("Onaylı profil URL'si boş olamaz")
          setSaving(false)
          return
        }
        if (!settings.unapprovedProfileUrl.trim()) {
          setError("Onaysız profil URL'si boş olamaz")
          setSaving(false)
          return
        }
      }

      const updatedSettings = await updateWhatsAppButtonSettings(settings)
      setSettings(updatedSettings)
      setSuccessMessage("Ayarlar başarıyla kaydedildi!")
      setTimeout(() => setSuccessMessage(null), 3000)
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(`Kaydetme başarısız: ${getErrorResponse(error) || "Bilinmeyen hata"}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="panel">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
            WhatsApp Buton Yönetimi
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            Profil sayfasında gösterilecek WhatsApp katılma butonu ayarlarını yönetin
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded">
            {successMessage}
          </div>
        )}

        {/* Visibility Toggle */}
        <div className="mb-8 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
          <label className="flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="isVisible"
              checked={settings.isVisible}
              onChange={handleInputChange}
              className="w-5 h-5 text-primary rounded"
            />
            <span className="ml-3 text-gray-700 dark:text-gray-300 font-medium">
              Buton Görünür Olsun
            </span>
          </label>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            Bu seçeneği etkinleştirdiğinizde, profil sayfasında WhatsApp butonu görüntülenecektir.
          </p>
        </div>

        {/* URL Inputs */}
        {settings.isVisible && (
          <div className="space-y-6">
            {/* Approved Profile URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Onaylı Profiller için WhatsApp URL
              </label>
              <input
                type="url"
                name="approvedProfileUrl"
                value={settings.approvedProfileUrl}
                onChange={handleInputChange}
                placeholder="https://chat.whatsapp.com/..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Onaylı (verified) kullanıcılar bu URL&apos;ye yönlendirilecektir.
              </p>
            </div>

            {/* Unapproved Profile URL */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Onaysız Profiller için WhatsApp URL
              </label>
              <input
                type="url"
                name="unapprovedProfileUrl"
                value={settings.unapprovedProfileUrl}
                onChange={handleInputChange}
                placeholder="https://chat.whatsapp.com/..."
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Onaylanmamış (unverified) kullanıcılar bu URL&apos;ye yönlendirilecektir.
              </p>
            </div>
          </div>
        )}

        {/* Save Button */}
        <div className="mt-8 flex gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? "Kaydediliyor..." : "Değişiklikleri Kaydet"}
          </button>
        </div>
      </div>

      {/* Information Card */}
      <div className="panel bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <h3 className="font-semibold text-blue-900 dark:text-blue-300 mb-3">Bilgi</h3>
        <ul className="list-disc list-inside space-y-2 text-sm text-blue-800 dark:text-blue-300">
          <li>WhatsApp grup davet bağlantılarını buraya yapıştırın</li>
          <li>Onaylı ve onaysız profiller için farklı gruplar kullanabilirsiniz</li>
          <li>Buton görünürlüğü istediğiniz zaman kapatabilirsiniz</li>
          <li>
            URL formatı:{" "}
            <code className="bg-blue-200 dark:bg-blue-800 px-2 py-1 rounded">
              https://chat.whatsapp.com/XXXXX
            </code>
          </li>
        </ul>
      </div>
    </div>
  )
}

export default WhatsAppButtonManager
